// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Minute-level battery sampling while a round is in play. ANDROID PHONE ONLY.
//
// This measures WHOLE-DEVICE battery, not Goffy's share of it: Android exposes no per-app
// battery accounting to unprivileged apps (BatteryStats is a signature permission), and there
// is no API that will ever say "this app used 8 %". What makes the data useful is the context
// recorded alongside every sample — GPS capture on or off, foreground or background, which
// release — so drain can be attributed by differencing rather than by asking.
//
// Known and accepted: React Native timers do not fire while Android has the app backgrounded or
// dozing, so the phone series has gaps of minutes to hours when the phone is in a pocket. That
// is correct output, not a bug — gapS records it, and the levels either side still bound the
// drain across it. A foreground service purely for telemetry would cost more battery than the
// measurement is worth. (When course-mapping runs, its location foreground service keeps the
// runtime alive and the gaps mostly vanish — which is exactly why gpsActive is on every sample.)

import { AppState, Platform } from 'react-native';

import type {
  BatteryCapabilities,
  BatterySample,
  BatterySegment,
} from '../features/battery-telemetry/types';
import {
  enqueueBatterySegment,
  loadBatteryEnvelope,
  saveOpenBatterySegment,
  setBatteryInstallId,
  type OpenBatterySegment,
} from './batteryTelemetryQueue';
import { getDeviceModel, getPowerState, hasBatteryModule } from './deviceSignals';
import { createEventId } from './offlineQueue';

const SAMPLE_INTERVAL_MS = 60_000;

/** Whichever comes first. Sixty samples is an hour at the target cadence. */
const MAX_SAMPLES_PER_SEGMENT = 60;
const MAX_SEGMENT_MS = 60 * 60 * 1000;

export const isSupported = (): boolean => Platform.OS === 'android';

export const telemetryEnabled = process.env.EXPO_PUBLIC_BATTERY_TELEMETRY_DISABLED !== 'true';

/** Monotonic where available: a user changing the clock must not bend the series. */
const monotonicMs: () => number =
  typeof globalThis.performance?.now === 'function'
    ? () => globalThis.performance.now()
    : () => Date.now();

const clockSource: BatterySegment['clockSource'] =
  typeof globalThis.performance?.now === 'function' ? 'performanceNow' : 'wallClock';

type SampleContext = {
  appState: string;
  wsConnected: boolean;
  gpsActive: boolean;
  sensorsActive: boolean;
  holeNumber: number | null;
};

type Session = {
  open: OpenBatterySegment;
  context: () => SampleContext;
  timer: ReturnType<typeof setInterval> | null;
  appStateSubscription: { remove: () => void } | null;
};

let session: Session | null = null;

export type StartBatteryTelemetryResult =
  | 'started'
  | 'unsupported-platform'
  | 'disabled'
  | 'no-battery-module'
  | 'paused'
  | 'already-running';

export type StartBatteryTelemetryOptions = {
  roundId: string;
  appVersion: string | null;
  osVersion: string | null;
  context: () => SampleContext;
};

/**
 * Opens a sampling session, first recovering anything a previous process left behind.
 *
 * Safe to call repeatedly: a second call while a session is open is a no-op.
 */
export const startBatteryTelemetry = async (
  options: StartBatteryTelemetryOptions
): Promise<StartBatteryTelemetryResult> => {
  if (!isSupported()) return 'unsupported-platform';
  if (!telemetryEnabled) return 'disabled';
  // A JS bundle running against an older native build has no battery module; sampling nulls
  // for a whole round would add rows that say nothing.
  if (!hasBatteryModule()) return 'no-battery-module';
  if (session) return 'already-running';

  const stored = await loadBatteryEnvelope();
  if (stored.pausedUntilMs > Date.now()) return 'paused';

  const deviceSessionId = createEventId();
  const installId = stored.installId ?? createEventId();
  if (!stored.installId) {
    await setBatteryInstallId(installId);
  }

  // A segment left open by a dead process is sealed as "recovered" rather than continued: it
  // belongs to a session that no longer exists, and mixing its samples into a new one would
  // hide the restart a reader needs to see.
  let segmentSeq = 0;
  if (stored.open) {
    if (stored.open.samples.length > 0) {
      await sealSegment(stored.open, 'recovered');
    }
    if (stored.open.roundId === options.roundId) {
      segmentSeq = stored.open.segmentSeq + 1;
    }
  }

  const capabilities = await probeCapabilities();
  const open = openSegment({
    roundId: options.roundId,
    deviceSessionId,
    installId,
    segmentSeq,
    appVersion: options.appVersion,
    osVersion: options.osVersion,
    capabilities,
  });

  session = { open, context: options.context, timer: null, appStateSubscription: null };
  await saveOpenBatterySegment(open);

  session.timer = setInterval(() => {
    void sampleNow();
  }, SAMPLE_INTERVAL_MS);

  // A sample on every foreground/background transition pins both ends of a screen-off stretch,
  // which is where most of the interesting drain happens.
  session.appStateSubscription = AppState.addEventListener('change', () => {
    void sampleNow();
  });

  await sampleNow();
  return 'started';
};

/** Seals whatever is buffered, so a round shorter than an hour still produces an object. */
export const stopBatteryTelemetry = async (): Promise<void> => {
  const active = session;
  session = null;
  if (!active) return;

  if (active.timer !== null) clearInterval(active.timer);
  active.appStateSubscription?.remove();

  if (active.open.samples.length > 0) {
    await sealSegment(active.open, 'round-ended');
  }
  await saveOpenBatterySegment(null);
};

export const isBatteryTelemetryRunning = (): boolean => session !== null;

const probeCapabilities = async (): Promise<BatteryCapabilities> => {
  const power = await getPowerState();
  return {
    levelPct: power.levelPct !== null,
    // Instantaneous current, charge counter, voltage and temperature need BatteryManager, which
    // is not exposed to JS. The watch fills these; on the phone they are always null, and saying
    // so here is what stops a reader treating the gap as a fault.
    currentNowUa: false,
    chargeCounterUah: false,
    voltageMv: false,
    temperatureC: false,
  };
};

const openSegment = (input: {
  roundId: string;
  deviceSessionId: string;
  installId: string;
  segmentSeq: number;
  appVersion: string | null;
  osVersion: string | null;
  capabilities: BatteryCapabilities;
}): OpenBatterySegment => ({
  schemaVersion: 1,
  // Minted here rather than at seal time, and persisted with the segment, so a segment rebuilt
  // after a process death keeps its identity and a retry is recognised rather than duplicated.
  segmentId: createEventId(),
  deviceSessionId: input.deviceSessionId,
  segmentSeq: input.segmentSeq,
  roundId: input.roundId,
  platform: 'android-phone',
  installId: input.installId,
  deviceModel: getDeviceModel(),
  osVersion: input.osVersion,
  appVersion: input.appVersion,
  startedAtUtc: new Date().toISOString(),
  clockSource,
  sampleIntervalTargetS: SAMPLE_INTERVAL_MS / 1000,
  capabilities: input.capabilities,
  samples: [],
  startedAtMonotonicMs: monotonicMs(),
});

const sampleNow = async (): Promise<void> => {
  const active = session;
  if (!active) return;

  const power = await getPowerState();
  // The session can end while getPowerState awaits; a sample landing in a closed segment would
  // resurrect it.
  if (session !== active) return;

  const context = active.context();
  const t = Math.round((monotonicMs() - active.open.startedAtMonotonicMs) / 1000);
  const previous = active.open.samples[active.open.samples.length - 1];

  const sample: BatterySample = {
    t,
    w: Math.round(Date.now() / 1000),
    gapS: previous ? t - previous.t : null,
    levelPct: power.levelPct,
    currentNowUa: null,
    chargeCounterUah: null,
    voltageMv: null,
    temperatureC: null,
    charging: power.charging,
    lowPower: power.lowPower,
    appState: context.appState,
    wsConnected: context.wsConnected,
    gpsActive: context.gpsActive,
    sensorsActive: context.sensorsActive,
    holeNumber: context.holeNumber,
  };
  active.open.samples.push(sample);

  if (active.open.samples.length >= MAX_SAMPLES_PER_SEGMENT || t * 1000 >= MAX_SEGMENT_MS) {
    await sealSegment(active.open, 'full');
    active.open = openSegment({
      roundId: active.open.roundId,
      deviceSessionId: active.open.deviceSessionId,
      installId: active.open.installId,
      segmentSeq: active.open.segmentSeq + 1,
      appVersion: active.open.appVersion,
      osVersion: active.open.osVersion,
      capabilities: active.open.capabilities,
    });
  }

  await saveOpenBatterySegment(active.open);
};

const sealSegment = async (
  open: OpenBatterySegment,
  sealReason: BatterySegment['sealReason']
): Promise<void> => {
  const sealedAt = new Date().toISOString();
  const { startedAtMonotonicMs: _monotonic, ...rest } = open;

  const segment: BatterySegment = {
    ...rest,
    endedAtUtc: sealedAt,
    sealedAtUtc: sealedAt,
    sealReason,
    sampleCount: open.samples.length,
  };

  await enqueueBatterySegment(segment);
};

export const __resetBatteryTelemetryForTests = () => {
  if (session?.timer) clearInterval(session.timer);
  session?.appStateSubscription?.remove();
  session = null;
};
